<ng-container *ngIf="bell.enabled$ | async">
  <button type="button"
          class="bell-btn"
          [matMenuTriggerFor]="bellMenu"
          (menuOpened)="onOpened()"
          [matTooltip]="'NOTIFICATIONS.BELL_TOOLTIP' | translate"
          [attr.aria-label]="'NOTIFICATIONS.BELL_TOOLTIP' | translate">
    <i class="fas fa-bell" aria-hidden="true"></i>
    <ng-container *ngIf="bell.unreadCount$ | async as unread">
      <span class="bell-badge" *ngIf="unread > 0">{{ unread > 99 ? '99+' : unread }}</span>
    </ng-container>
  </button>
  <mat-menu #bellMenu="matMenu" xPosition="before" [class]="'header-ghost-menu bell-menu'">
    <div class="nf-head" (click)="$event.stopPropagation()">
      <span class="nf-title">{{ 'NOTIFICATIONS.TITLE' | translate }}</span>
      <!--
        Two shapes for the same fact, and the heading is never bare either way.

        Above zero it is the bare figure in the same red badge the bell icon wears, because that is
        what a count that needs attention should look like and repeating the word "unread" beside a
        red badge says nothing the badge has not already said.

        At zero there is no badge to read, so the sentence carries it: "Notifications  0 unread".
        That is the original decision and it still holds — an `*ngIf="… as unread"` here would drop
        the line entirely at zero (zero is falsy) and leave the panel headed by a lone title.

        The object literal is always truthy, so the container renders in both states while the async
        pipe stays subscribed once.
      -->
      <ng-container *ngIf="{ unread: (bell.unreadCount$ | async) ?? 0 } as vm">
        <span class="nf-counter" *ngIf="vm.unread > 0">
          <span class="nf-badge">{{ vm.unread > 99 ? '99+' : vm.unread }}</span>
          <!--
            UNREAD_LABEL, the bare word, and NOT UNREAD_COUNT. That key is the whole phrase
            "{{count}} unread" with the figure inside it, so reusing it here would print the number
            twice — once in the badge and once in the sentence beside it.

            The cost is that this layout fixes the order as figure-then-word, which a locale that
            puts them the other way round cannot undo. Accepted knowingly: the zero branch below
            still renders UNREAD_COUNT whole, so the phrase a translator controls survives where
            there is no badge to anchor the order.
          -->
          <span class="nf-count">{{ 'NOTIFICATIONS.UNREAD_LABEL' | translate }}</span>
        </span>
        <span class="nf-count" *ngIf="vm.unread === 0">
          {{ 'NOTIFICATIONS.UNREAD_COUNT' | translate: { count: 0 } }}
        </span>
      </ng-container>
      <span class="spacer"></span>
      <button type="button" class="nf-link" (click)="markAll()">
        {{ 'NOTIFICATIONS.MARK_ALL_READ' | translate }}
      </button>
    </div>
    <div class="nf-list">
      <button type="button"
              mat-menu-item
              class="nf-item"
              *ngFor="let item of items; trackBy: trackById"
              [class.unread]="item.status === 'sent'"
              (click)="open(item)">
        <span class="nf-ico" [ngClass]="'sev-' + item.severity">
          <i class="fas" [ngClass]="severityIcon(item.severity)" aria-hidden="true"></i>
        </span>
        <span class="nf-mid">
          <span class="nf-sub">{{ item.subject }}</span>
          <!-- One meta line, exactly as the design's BellMenu renders it:
               "{product} - {event label}", a 3px dot, then the relative time. The product is TEXT
               here, not the tinted square the /notifications rows use - the bell panel has no
               product mark in the design. -->
          <span class="nf-meta">
            <span class="nf-src">{{ sourceLine(item) }}</span>
            <span class="dot" aria-hidden="true"></span>
            <span class="nf-when">{{ relativeTime(item.createdAt) }}</span>
          </span>
        </span>
        <span class="nf-dot" *ngIf="item.status === 'sent'"></span>
      </button>
      <!--
        Shown while the feed is in flight. `loading` already existed and nothing rendered it, so on
        a slow connection the panel opened blank and then filled - indistinguishable from "nothing
        yet" for as long as the request took. It sits BEFORE the items rather than replacing them:
        on a re-open the previous page is still valid, and blanking it would be a step backwards.
      -->
      <div class="nf-loading" *ngIf="loading">
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
        <!-- GENERIC.LOADING, not a new NOTIFICATIONS key: it already exists and is genuinely
             translated in all 11 locales ("Cargando", "Wird geladen", ...), where a key added now
             would have shipped English everywhere until someone translated it. -->
        <span>{{ 'GENERIC.LOADING' | translate }}</span>
      </div>
      <div class="nf-empty" *ngIf="!loading && items.length === 0">
        {{ 'NOTIFICATIONS.EMPTY_PANEL' | translate }}
      </div>
    </div>
    <div class="nf-foot">
      <button type="button" mat-menu-item class="nf-link" (click)="goToAll()">
        {{ 'NOTIFICATIONS.VIEW_ALL' | translate }}
      </button>
      <button type="button" mat-menu-item class="nf-link muted" (click)="goToSettings()">
        {{ 'NOTIFICATIONS.SETTINGS_LINK' | translate }}
      </button>
    </div>
  </mat-menu>
</ng-container>
